"use client";

import { useCallback, useEffect, useState } from "react";

export const QUALITY_FLAGS_KEY = "admin-quality-flags";
export const QUALITY_FLAGS_EVENT = "quality-flags-changed";

export interface QualityFlags {
  /**
   * Custom query relevance weight (0–100). null = use server default.
   * No sidebar control — set transiently by the debug "scenario" toggle and
   * consumed by the search-results debug panel.
   */
  search_query_weight: number | null;
  /** Custom quality weight (0–100). null = use server default. Debug scenario/panel only (see above). */
  search_quality_weight: number | null;
  /** Search result count. null = use server default (100). */
  search_top_k: number | null;
  /** Pool multiplier: vector search returns top_k × multiplier for quality reranking. null = use server default (10). */
  candidate_pool_multiplier: number | null;
}

export const DEFAULT_FLAGS: QualityFlags = {
  search_query_weight: null,
  search_quality_weight: null,
  search_top_k: null,
  candidate_pool_multiplier: null,
};

function readFlags(): QualityFlags {
  try {
    const raw = localStorage.getItem(QUALITY_FLAGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<QualityFlags>;
      // Only keep known keys (strip legacy fields from old localStorage)
      const clean: Partial<QualityFlags> = {};
      for (const key of Object.keys(DEFAULT_FLAGS) as (keyof QualityFlags)[]) {
        if (key in parsed) {
          (clean as Record<string, unknown>)[key] = (parsed as Record<string, unknown>)[key];
        }
      }
      return { ...DEFAULT_FLAGS, ...clean };
    }
  } catch {
    // localStorage unavailable or corrupted
  }
  return { ...DEFAULT_FLAGS };
}

function writeFlags(flags: QualityFlags): void {
  try {
    localStorage.setItem(QUALITY_FLAGS_KEY, JSON.stringify(flags));
    window.dispatchEvent(new Event(QUALITY_FLAGS_EVENT));
  } catch {
    // localStorage unavailable
  }
}

function flagsEqual(a: QualityFlags, b: QualityFlags): boolean {
  return (
    a.search_query_weight === b.search_query_weight &&
    a.search_quality_weight === b.search_quality_weight &&
    a.search_top_k === b.search_top_k &&
    a.candidate_pool_multiplier === b.candidate_pool_multiplier
  );
}

export function useQualityFlags() {
  const [flags, setFlags] = useState<QualityFlags>({ ...DEFAULT_FLAGS });

  // Hydrate from localStorage on mount
  useEffect(() => {
    setFlags(readFlags());
  }, []);

  // Listen for cross-tab and same-tab sync events
  useEffect(() => {
    function sync() {
      setFlags(readFlags());
    }
    window.addEventListener("storage", sync);
    window.addEventListener(QUALITY_FLAGS_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(QUALITY_FLAGS_EVENT, sync);
    };
  }, []);

  const setFlag = useCallback(
    <K extends keyof QualityFlags>(key: K, value: QualityFlags[K]) => {
      setFlags((prev) => {
        const next = { ...prev, [key]: value };
        writeFlags(next);
        return next;
      });
    },
    []
  );

  const resetAll = useCallback(() => {
    const defaults = { ...DEFAULT_FLAGS };
    setFlags(defaults);
    writeFlags(defaults);
  }, []);

  const isDefault = flagsEqual(flags, DEFAULT_FLAGS);

  return { flags, setFlag, resetAll, isDefault };
}

/**
 * Standalone reader that returns only non-default flag values.
 * Used by search API to send overrides as query params (only differing values).
 */
export function getQualityFlagOverrides(): Partial<QualityFlags> {
  const flags = readFlags();
  const overrides: Partial<QualityFlags> = {};

  if (flags.search_query_weight !== null) overrides.search_query_weight = flags.search_query_weight;
  if (flags.search_quality_weight !== null) overrides.search_quality_weight = flags.search_quality_weight;
  if (flags.search_top_k !== null) overrides.search_top_k = flags.search_top_k;
  if (flags.candidate_pool_multiplier !== null) overrides.candidate_pool_multiplier = flags.candidate_pool_multiplier;

  return overrides;
}
